<template>
    <card-normal>
        <template #sections>
            <card-section area="card-header">
                <text-decorator bold>
                    Status
                </text-decorator>
            </card-section>
        </template>

        <flex-container :gap="Size.sm">
            <div v-if="!props.editable || !stateIsEditable">
                <dot-block v-if="props.order" :color="getStatusColor(props.order.status)">
                    <template #title>
                        {{ getStatusLabel(currentStatus) }}
                    </template>
                </dot-block>
            </div>
            <template v-else>
                <input-group
                    v-model="currentStatus"
                >
                    <template #input>
                        <select-input
                            :items="statusses ?? []"
                            variant="inline"
                        >
                            <template #active>
                                <dot-block :color="getStatusColor(currentStatus)">
                                    <template #title>
                                        {{ getStatusLabel(currentStatus) }}
                                    </template>
                                </dot-block>
                            </template>

                            <template #item="{ bindings }">
                                <dot-block :color="getStatusColor(bindings.item)">
                                    <template #title>
                                        {{ getStatusLabel(bindings.item) }}
                                    </template>
                                </dot-block>
                            </template>
                        </select-input>
                    </template>
                </input-group>
                <transition name="fade">
                    <flex-container
                        v-if="statusHasChanges"
                        :gap="Size.sm"
                        justify-content="flex-end"
                    >
                        <button-outline @click="cancelStatus">
                            Annuleren
                        </button-outline>
                        <button-block @click="submitStatus">
                            Opslaan
                        </button-block>
                    </flex-container>
                </transition>
            </template>
        </flex-container>
    </card-normal>
</template>

<script setup lang="ts">
    import {
        DotBlock,
        CardNormal,
        CardSection,
        Color,
        TextDecorator,
        Size,
        ButtonOutline,
        FlexContainer,
        InputGroup,
        SelectInput,
        ButtonBlock,
    } from '@keyvue/one-ui';

    import { Order, OrderAPI, OrderStatus } from '@keyvue/one-api-client-legacy';
    import { computed, ref, watch } from 'vue';
    import type { Ref } from 'vue';
    import useNotify from '@composables/useNotify';
    import useEventBus from '@composables/useEventBus';

    export interface Props {
        order: Order,
        assignableStatusses: OrderStatus[]
        editable?: boolean,
    }

    const props = defineProps<Props>();
    const { notifySaving, notifySaved } = useNotify();
    const eventBus = useEventBus();

    const statusHasChanges = ref(false);
    const currentStatus = ref(props.order.status) as Ref<OrderStatus>;
    const statusses = ref(props.assignableStatusses);

    const stateIsEditable = computed(() => props.order.status === OrderStatus.PENDING);

    const getStatusColor = (status: OrderStatus) => {
        if (status === OrderStatus.SUCCESSFUL) {
            return Color.green500;
        }

        if (status === OrderStatus.UNSUCCESSFUL) {
            return Color.red500;
        }

        return Color.gray400;
    };

    const getStatusLabel = (status: OrderStatus) => {
        if (status === OrderStatus.SUCCESSFUL) {
            return 'Succesvol';
        }

        if (status === OrderStatus.UNSUCCESSFUL) {
            return 'Niet succesvol';
        }

        return 'Lopend';
    };

    const submitStatus = async () => {
        await changeStatus(currentStatus.value);

        statusHasChanges.value = false;
    };

    const cancelStatus = () => {
        currentStatus.value = props.order.status;
        statusHasChanges.value = false;
    };

    watch(currentStatus, () => {
        statusHasChanges.value = currentStatus.value !== props.order.status;
    });

    watch(props, () => {
        currentStatus.value = props.order.status;
    });

    const changeOrderField = async (callback: (o: Order) => Order) => {
        const closeNotification = notifySaving(undefined, 0);
        const legacyOrder = await OrderAPI.get(props.order.id);
        await OrderAPI.update(props.order.id, callback(legacyOrder));
        notifySaved();
        if (closeNotification) {
            closeNotification();
        }
    };

    const changeStatus = async (status: OrderStatus) => {
        await changeOrderField((o: Order) => {
            const changedOrder = o;
            changedOrder.status = status;
            return changedOrder;
        });

        eventBus.dispatch('resource-manipulated', 'Order');
    };
</script>

<style lang="scss" scoped>
.state-card-border {
    border-top: 2px solid color(gray-200);
}
</style>
